/**
 * Address Parser Adapter
 *
 * This module provides a unified interface for parsing US addresses.
 * Uses @sroussey/parse-address for parsing.
 */

import { AddressParser as SrousseyAddressParser } from '@sroussey/parse-address'

/**
 * Parsed address result interface
 *
 * Only includes fields actually used by consumers:
 * - PropertyTaxPayload.ts: street, number
 * - CoreLogicService.ts: street, number, short_street_type
 */
export interface ParsedAddress {
  /** House/building number (e.g., "123", "3813 1/2") */
  number?: string
  /** Street name without number or type (e.g., "Main", "Oak") */
  street?: string
  /** 2-character street type code for CoreLogic API (e.g., "ST", "AV", "DR") */
  short_street_type?: string
  // Fallback fields from bankrate parser for street type
  short_street_type1?: string
  short_street_type2?: string
}

export interface FreeformAddressComponents {
  street?: string
  apt?: string
  city?: string
  state?: string
  zipCode?: string
}

interface RawParsedLocation {
  number?: string
  prefix?: string
  street?: string
  type?: string
  suffix?: string
  sec_unit_type?: string
  sec_unit_num?: string
  city?: string
  state?: string
  postal_code?: string
}

/**
 * Maps street type to 2-character short code for CoreLogic API
 * Used by bankrate parser which doesn't provide short codes directly
 */
export const STREET_TYPE_TO_SHORT_CODE: Record<string, string> = {
  Aly: 'AL',
  Anx: 'AN',
  Arc: 'AR',
  Ave: 'AV',
  Byu: 'BA',
  Bch: 'BC',
  Bldg: 'BD',
  Bnd: 'BE',
  Blf: 'BF',
  Brk: 'BK',
  Br: 'BN',
  Btm: 'BO',
  Brg: 'BR',
  Blvd: 'BV',
  Byp: 'BY',
  Cp: 'CA',
  Ctr: 'CE',
  Xing: 'CG',
  Cir: 'CI',
  Crk: 'CK',
  Clb: 'CL',
  Cor: 'CN',
  Crse: 'CO',
  Cpe: 'CP',
  Cres: 'CS',
  Ct: 'CT',
  Cv: 'CV',
  Cswy: 'CW',
  Cyn: 'CY',
  Dl: 'DA',
  Dm: 'DM',
  Dr: 'DR',
  Dv: 'DV',
  Est: 'ES',
  Expy: 'EX',
  Fall: 'FA',
  Falls: 'FA',
  Fry: 'FE',
  Frg: 'FG',
  Fld: 'FI',
  Frk: 'FK',
  Flt: 'FL',
  Frd: 'FO',
  Frst: 'FT',
  Fwy: 'FY',
  Gdn: 'GA',
  Gdns: 'GA',
  Gln: 'GL',
  Grn: 'GN',
  Grv: 'GR',
  Gtwy: 'GW',
  Hbr: 'HB',
  Hl: 'HL',
  Hls: 'HL',
  Hts: 'HT',
  Hvn: 'HV',
  Holw: 'HW',
  Hwy: 'HY',
  Is: 'ID',
  Inlt: 'IN',
  Jct: 'JC',
  Knl: 'KN',
  Ky: 'KY',
  Lk: 'LA',
  Lks: 'LA',
  Land: 'LD',
  Lndg: 'LD',
  Lf: 'LF',
  Ldg: 'LG',
  Lgt: 'LI',
  Ln: 'LN',
  Loop: 'LP',
  Lck: 'LO',
  Mall: 'MA',
  Mdw: 'ME',
  Mdws: 'ME',
  Ml: 'ML',
  Mls: 'ML',
  Mtn: 'MN',
  Mt: 'MO',
  Mnr: 'MR',
  Msn: 'MS',
  Nck: 'NK',
  Orch: 'OR',
  Oval: 'OV',
  Path: 'PA',
  Pike: 'PI',
  Park: 'PK',
  Pl: 'PL',
  Pln: 'PN',
  Plns: 'PN',
  Prt: 'PO',
  Pr: 'PR',
  Pne: 'PS',
  Pt: 'PT',
  Pkwy: 'PY',
  Plz: 'PZ',
  Radl: 'RA',
  Rnch: 'RC',
  Rd: 'RD',
  Rds: 'RD',
  Rst: 'RE',
  Rdg: 'RI',
  Run: 'RN',
  Row: 'RO',
  Rpd: 'RP',
  Rpds: 'RP',
  Rte: 'RT',
  Rue: 'RU',
  Riv: 'RV',
  Stra: 'SA',
  Spg: 'SG',
  Spgs: 'SG',
  Shr: 'SH',
  Shl: 'SL',
  Strm: 'SM',
  Sta: 'SN',
  Spur: 'SP',
  Sq: 'SQ',
  St: 'ST',
  Sts: 'ST',
  Smt: 'SU',
  Trce: 'TC',
  Ter: 'TE',
  Trak: 'TK',
  Tunl: 'TN',
  Tpke: 'TP',
  Trl: 'TR',
  Trwy: 'TU',
  Trfy: 'TW',
  Un: 'UN',
  Vly: 'VA',
  Vlg: 'VG',
  Via: 'VI',
  Vl: 'VL',
  Vis: 'VS',
  Vw: 'VW',
  Wl: 'WE',
  Wls: 'WE',
  Walk: 'WK',
  Way: 'WY',
}

/**
 * Get short code for street type
 */
export function getShortCode(streetType?: string): string {
  if (!streetType) return 'BL'
  // Try exact match first
  if (STREET_TYPE_TO_SHORT_CODE[streetType]) {
    return STREET_TYPE_TO_SHORT_CODE[streetType]
  }
  // Try title case
  const titleCase =
    streetType.charAt(0).toUpperCase() + streetType.slice(1).toLowerCase()
  return STREET_TYPE_TO_SHORT_CODE[titleCase] || 'BL'
}

// Singleton instances of parser
const srousseyParser = new SrousseyAddressParser()

/**
 * Parse an address string into structured components
 * @param address The address string to parse
 * @returns ParsedAddress object or undefined if parsing fails
 */
export async function parseLocation(
  address: string
): Promise<ParsedAddress | undefined> {
  const result = srousseyParser.parseLocation(address)
  if (!result || !result.street) {
    return undefined
  }
  return result
}

/**
 * Retries incomplete parses with comma segments dropped — trailing segments
 * first, then contiguous interior runs, fewest drops first — recovering
 * duplicated "city, state zip" tails and extra place segments (e.g.
 * townships) that break the parser. Interior candidates keep the street
 * segment intact, so their parsed city must come from a later segment —
 * never stolen from the street itself ("Aspen Road" parsing as city "Road").
 */
function parseLocationWithRetry(
  address: string
): RawParsedLocation | undefined {
  const first: RawParsedLocation | undefined =
    srousseyParser.parseLocation(address)
  if (first?.street && first.city && first.state) {
    return first
  }

  const segments = address.split(',').map((segment) => segment.trim())
  const candidates: { text: string; interior: boolean }[] = []

  for (let end = segments.length - 1; end >= 1; end--) {
    candidates.push({
      text: segments.slice(0, end).join(', '),
      interior: false,
    })
  }
  for (let runLength = 1; runLength <= segments.length - 2; runLength++) {
    for (let start = 1; start + runLength <= segments.length - 1; start++) {
      candidates.push({
        text: [
          ...segments.slice(0, start),
          ...segments.slice(start + runLength),
        ].join(', '),
        interior: true,
      })
    }
  }

  for (const { text, interior } of candidates) {
    const result: RawParsedLocation | undefined =
      srousseyParser.parseLocation(text)
    if (!result?.street || !result.city || !result.state) {
      continue
    }
    if (
      interior &&
      !text
        .slice(segments[0].length)
        .toLowerCase()
        .includes(result.city.toLowerCase())
    ) {
      continue
    }
    return result
  }

  return first
}

export async function parseFreeformAddress(
  address: string
): Promise<FreeformAddressComponents> {
  const result = parseLocationWithRetry(address)

  if (!result) {
    return {}
  }

  const streetParts = [
    result.number,
    result.prefix,
    result.street,
    result.type,
    result.suffix,
  ].filter(Boolean)

  return {
    street: streetParts.length ? streetParts.join(' ') : undefined,
    apt: result.sec_unit_num || undefined,
    city: result.city || undefined,
    state: result.state || undefined,
    zipCode: result.postal_code?.split('-')[0] || undefined,
  }
}
